import { collection, limit, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from './firebase';
import type { TxType } from './logTransaction';

interface TxRecord {
  type: TxType;
  hash: string;
  account: string;
  chainId: number;
  details: Record<string, string>;
  createdAt: Timestamp | null;
}

export interface BridgeEvent {
  type: 'bridgeLock' | 'bridgeUnlock';
  hash: string;
  account: string;
  amount: number;
  target?: string;
  at: number;
}

export interface BridgeActivity {
  recent: BridgeEvent[];
  count24h: number;
  volume24h: number;
  totalCount: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Streams the shared transactions collection (read: if true) and keeps only
// bridge lock/unlock events, deriving recent list + 24h rollups client-side.
export function useBridgeActivity(max = 8): BridgeActivity | null {
  const [activity, setActivity] = useState<BridgeActivity | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(500));
    return onSnapshot(q, (snapshot) => {
      const events: BridgeEvent[] = [];
      const cutoff = Date.now() - DAY_MS;
      let count24h = 0;
      let volume24h = 0;

      for (const doc of snapshot.docs) {
        const tx = doc.data() as TxRecord;
        if (tx.type !== 'bridgeLock' && tx.type !== 'bridgeUnlock') continue;
        const at = tx.createdAt?.toMillis() ?? 0;
        const amount = Number(tx.details?.amount ?? '0') || 0;
        events.push({ type: tx.type, hash: tx.hash, account: tx.account, amount, target: tx.details?.target, at });
        if (at >= cutoff) {
          count24h += 1;
          volume24h += amount;
        }
      }

      setActivity({
        recent: events.slice(0, max),
        count24h,
        volume24h,
        totalCount: events.length,
      });
    });
  }, [max]);

  return activity;
}
